import { MOTORES, MotorSchema, motorNaoTratado, type Motor, type ProvedorOpencode } from '@remoto/protocolo';
import { provedorDoModelo, type VeredictoDeCapacidade } from '@remoto/shared';
import { motorFechado, type MotorFechado } from './criacao-por-motor';

/**
 * O motor de uma conversa JÁ CRIADA, como a conversa e a lista o mostram (F17).
 *
 * Mora fora dos componentes pela mesma razão de `criacao-por-motor.ts`: a guarda
 * dos ramos binários (`packages/shared/tests/ramos-de-motor.spec.ts`). Com três
 * motores, `motor === 'CODEX' ? 'CODEX' : 'CLAUDE'` — o que o cabeçalho fazia —
 * mandava a conversa do OpenCode para o selo, o catálogo e a troca do Claude,
 * calada. Aqui toda decisão por motor é `switch` que termina em
 * `motorNaoTratado`.
 */

/**
 * O motor da linha que a API mandou.
 *
 * Ausente é CLAUDE: é a regra de todo o fio (`MotorSchema.default`), e é o que
 * as conversas de antes do segundo motor são. Mas um valor PRESENTE e
 * desconhecido (uma API mais nova que o painel) NÃO vira Claude: o selo diria
 * "Claude Code" sobre outra coisa, e o seletor ofereceria `opus` a um motor que
 * não o conhece. Fica `null` — sem selo, sem catálogo, sem troca —, que é a
 * tela dizendo "não sei" em vez de chutar. Mesma regra do `motorDoPedido`.
 */
export function motorDaLinha(bruto: string | null | undefined): Motor | null {
  const lido = MotorSchema.safeParse(bruto ?? undefined);
  return lido.success ? lido.data : null;
}

/**
 * O provedor de modelos de uma conversa (F2): o do id do modelo dela, e só no
 * OpenCode — Claude e Codex não têm provedor de modelos, e `provedorDoModelo`
 * leria `opus` como "OpenRouter" (todo id sem o prefixo do NanoGPT é).
 *
 * Do MODELO da conversa, e não do ativo da frota: é para lá que o conteúdo
 * dela foi. Quando a API troca o modelo de uma conversa antiga (D46), a linha
 * muda junto, e o cabeçalho acompanha. Sem modelo, `null` ("não sei").
 */
export function provedorDaConversa(motor: Motor | null, modelo: string | null | undefined): ProvedorOpencode | null {
  if (motor === null) return null;
  switch (motor) {
    case 'CLAUDE':
    case 'CODEX':
      return null;
    case 'OPENCODE':
      return modelo ? provedorDoModelo(modelo) : null;
    default:
      return motorNaoTratado(motor);
  }
}

/** Como o selo de um motor se desenha. */
export interface SeloDoMotor {
  /**
   * A cor, em classes do Tailwind. É o reforço, nunca a informação: o selo é
   * TEXTO (`ROTULO_DO_MOTOR`), porque quem não distingue as cores — ou lê por
   * leitor de tela — ficaria sem saber qual motor atende a conversa.
   */
  cor: string;
  /**
   * Se a LISTA de sessões mostra o selo. Só o que foge do padrão da casa: numa
   * frota de trinta conversas do Claude, marcar todas faria a etiqueta virar
   * textura, e o que se precisa ver é a que não é.
   */
  naLista: boolean;
}

export function seloDoMotor(motor: Motor): SeloDoMotor {
  switch (motor) {
    case 'CLAUDE':
      return { cor: 'bg-zinc-800 text-zinc-400', naLista: false };
    case 'CODEX':
      return { cor: 'bg-emerald-900/60 text-emerald-300', naLista: true };
    case 'OPENCODE':
      // Céu, e não âmbar: o âmbar é do selo "sai da rede de casa", que mora ao
      // lado — duas peças da mesma cor pareceriam uma só.
      return { cor: 'bg-sky-900/60 text-sky-300', naLista: true };
    default:
      return motorNaoTratado(motor);
  }
}

/** Um destino possível de uma troca de motor, aberto ou com o motivo de não estar. */
export interface DestinoDaTroca {
  motor: Motor;
  fechado: MotorFechado | null;
}

/**
 * Para onde uma conversa pode continuar: TODOS os outros motores, na ordem da
 * casa, cada um com o motivo de estar fechado — ou `null` quando está aberto.
 *
 * Com dois motores o destino era "o outro", e não havia o que escolher. Com
 * três, a escolha existe, e a regra de quem está fechado é a MESMA da tela de
 * criar (`motorFechado`): o Codex pela sonda, o OpenCode pelo recurso, pela VPS
 * (D1), pela sonda e pela chave. Uma segunda régua aqui divergiria da primeira
 * na primeira revisão — e a troca é, por dentro, uma criação.
 *
 * O destino fechado NÃO some: aparece desabilitado, com o motivo, porque
 * "sumiu" não ensina nada a quem precisa ligar a chave.
 */
export function destinosDaTroca(
  origem: Motor,
  contexto: { codex: VeredictoDeCapacidade | null; motivoOpencode: string | null },
): DestinoDaTroca[] {
  return MOTORES.filter((m) => m !== origem).map((motor) => ({
    motor,
    fechado: motorFechado(motor, contexto),
  }));
}

/** O primeiro destino aberto, que é o que o modal marca ao abrir. */
export function primeiroDestinoAberto(destinos: readonly DestinoDaTroca[]): Motor | null {
  return destinos.find((d) => d.fechado === null)?.motor ?? null;
}
